import logger from "@rentiohq/shared-frontend/dist/logger";

const PENDING_ACCOUNT_SWITCH_KEY = "rentio.pendingAccountSwitchAccountId";

/**
 * Switching the active account mints a new token and then hard-reloads the app.
 * The `user` in the auth slice is persisted (see `auth.reducerPersist.ts`), so
 * the reloaded app boots on the user of the account that was just left - with
 * the previous `brokerId` - until `/users/me` answers. Everything keyed on that
 * id is then aimed at a broker the new token cannot see.
 *
 * The switch therefore records the account it is switching to, and the app reads
 * it once on the next boot to tell a stale rehydrated user from a current one.
 * `localStorage` is used directly rather than the async storage helpers so the
 * value is available on the very first render, which is the one that matters.
 */
export const setPendingAccountSwitch = (accountId: number) => {
  try {
    localStorage.setItem(PENDING_ACCOUNT_SWITCH_KEY, String(accountId));
  } catch (unknownError) {
    logger.logError({ error: unknownError as Error });
  }
};

let hasConsumed = false;
let consumedAccountId: number | undefined;

/**
 * Reads the pending switch and clears it from storage, so the next page load
 * starts clean. Within this page load the answer is cached: every caller, and
 * every remount of a caller, has to see the same account for as long as the
 * rehydrated user has not caught up.
 */
export const consumePendingAccountSwitch = (): number | undefined => {
  if (hasConsumed) {
    return consumedAccountId;
  }

  hasConsumed = true;

  try {
    const stored = localStorage.getItem(PENDING_ACCOUNT_SWITCH_KEY);
    if (stored === null) {
      return undefined;
    }

    localStorage.removeItem(PENDING_ACCOUNT_SWITCH_KEY);

    const accountId = Number(stored);
    consumedAccountId = Number.isFinite(accountId) ? accountId : undefined;
  } catch (unknownError) {
    logger.logError({ error: unknownError as Error });
  }

  return consumedAccountId;
};
